import { WherebyCallKind } from '../../../../pure-js/enums/Whereby'
import type { WherebyCallScope } from '../../../../pure-js/types/WherebyTypes'
import { resolveWherebyCallKind } from '../../../../pure-js/libs/wherebyCallOwnership'
import type { CallScope } from '../../store/slices/wherebySlice'
import { GamerRoutes, SharedRoutes } from '../../config/routes/routeEnums'

// What each call surface tells the ownership claim it is connected to. Kept together (and out of the
// components) so every surface describes its call the same way.

// The page a call can be joined from, so a takeover on another tab/device resumes it in the right place.
// Only the game and the AI coach have a page of their own; a team or sprint call lives wherever the user is.
export const resolveCallRoute = (kind: WherebyCallKind, currentPathname: string): string => {
  const routePrefix = currentPathname.startsWith('/facilitator') ? '/facilitator' : '/gamer'
  const ROUTE_BY_KIND: Record<WherebyCallKind, string> = {
    [WherebyCallKind.GAME]: `${routePrefix}${SharedRoutes.PLAY_GAME_WITHOUT_INTRODUCTION}`,
    [WherebyCallKind.AI_COACH]: GamerRoutes.AI_COACH,
    [WherebyCallKind.TEAM]: currentPathname,
    [WherebyCallKind.SPRINT]: currentPathname
  }
  return ROUTE_BY_KIND[kind]
}

/**
 * Floating call: the pinned activity call when there is one (game / AI coach / sprint), otherwise the shared
 * team call on the page the user is on.
 */
export const buildFloatingCallScope = (params: {
  teamId?: string
  teamName?: string
  callScope: CallScope | null
  currentPathname: string
}): WherebyCallScope | null => {
  const { teamId, teamName, callScope, currentPathname } = params
  if (!teamId) return null

  const kind = callScope?.kind ?? WherebyCallKind.TEAM
  return {
    kind,
    teamId,
    teamName,
    sprintId: callScope?.sprintId,
    activityId: callScope?.activityId,
    activityType: callScope?.activityType,
    sessionId: callScope?.sessionId,
    route: resolveCallRoute(kind, currentPathname)
  }
}

/** AI coach page: the debrief call of the coach session being played. */
export const buildAiCoachCallScope = (params: {
  session?: { teamId?: string; sprintId?: string; activityId?: string; sessionId?: string }
  teamName?: string
  route: string
}): WherebyCallScope | null => {
  const { session, teamName, route } = params
  if (!session?.teamId) return null

  return {
    kind: WherebyCallKind.AI_COACH,
    teamId: session.teamId,
    teamName,
    sprintId: session.sprintId,
    activityId: session.activityId,
    sessionId: session.sessionId,
    route
  }
}

/**
 * Dashboard / game-center embed: the activity's own room when the resolved activity has one, otherwise
 * the shared team room. The call lives on the page the embed is on.
 */
export const buildEmbeddedCallScope = (params: {
  teamId?: string
  sprintId?: string
  activityId?: string
  activityType?: string
  isActivityScope: boolean
  currentPathname: string
}): WherebyCallScope | null => {
  const { teamId, sprintId, activityId, activityType, isActivityScope, currentPathname } = params
  if (!teamId) return null

  return {
    kind: resolveWherebyCallKind(isActivityScope ? activityType : undefined),
    teamId,
    sprintId,
    activityId,
    activityType,
    route: currentPathname
  }
}

/** Sprint call page: the call of the whole sprint, on the page that holds it. */
export const buildSprintCallScope = (params: {
  teamId?: string
  sprintId?: string
  activityId?: string
  activityType?: string
  route: string
}): WherebyCallScope | null => {
  const { teamId, sprintId, activityId, activityType, route } = params
  if (!teamId || !sprintId || !activityId) return null

  return { kind: WherebyCallKind.SPRINT, teamId, sprintId, activityId, activityType, route }
}
